import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import connect from 'components/lib/connect';
import ErrorTooltip from './error_tooltip';
import Err from 'models/err';
import MergeProfile from 'models/modal/merge_profile';
import { normalizeEmailAddress } from 'models/customer_email_address';
import { normalizePhoneNumber } from 'models/phone_number';
import OpenModal from 'actions/modal/open_modal';
import ShowMatches from 'actions/customer/show_matches';
import Tooltip from 'components/common/tooltip';
import TrackTooltipShown from 'actions/customer_match/track_tooltip_shown';

const MATCHING_TEXT = 'Matching customers found!';
const VIEW_MATCH = 'View matches';
const CLEAR_ENTRY = 'Clear entry';

/**
 * MergeableErrorTooltip will, if one of the errors is a TAKEN error, display a special tooltip
 * which will allow users to merge the customer that already possesses that piece of contact
 * information.
 */
export class MergeableErrorTooltip extends PureComponent {
  constructor(props) {
    super(props);
    this.handleClickClear = this.handleClickClear.bind(this);
    this.handleClickViewMatch = this.handleClickViewMatch.bind(this);
  }

  componentDidMount() {
    if (this.props.showTooltip && this.props.onTooltipRender) {
      this.props.onTooltipRender(this.props.customerId);
    }
  }

  componentDidUpdate(prevProps) {
    const hasCustomerChanged = this.props.customerId !== prevProps.customerId;
    // if we weren't showing the tooltip before but are now
    if (this.props.showTooltip && (hasCustomerChanged || !prevProps.showTooltip)) {
      if (this.props.onTooltipRender) {
        this.props.onTooltipRender(this.props.customerId);
      }
    }
  }

  render() {
    const { children, className, 'data-aid': aid, errors, isModalOpen, showClearButton, showTooltip } = this.props;
    if (!showTooltip) {
      return (
        <ErrorTooltip className={className} data-aid={aid} errors={errors}>
          {children}
        </ErrorTooltip>
      );
    }

    const message = (
      <div className="contactField-mergeableErrorTooltip-matchingCustomer">
        <span>{MATCHING_TEXT}</span>
        <span className="contactField-mergeableErrorTooltip-matchingCustomer-actions">
          <span
            className="contactField-mergeableErrorTooltip-matchingCustomer-action contactField-mergeableErrorTooltip-matchingCustomer-action-viewMatch"
            onMouseDown={preventDefault}
            onClick={this.handleClickViewMatch}
          >
            {VIEW_MATCH}
          </span>
          {showClearButton && (
            <span
              className="contactField-mergeableErrorTooltip-matchingCustomer-action contactField-mergeableErrorTooltip-matchingCustomer-action-clearEntry"
              onMouseDown={preventDefault}
              onClick={this.handleClickClear}
            >
              {CLEAR_ENTRY}
            </span>
          )}
        </span>
      </div>
    );

    return (
      <Tooltip
        className={classnames(this.props.className, 'contactField-mergeableErrorTooltip')}
        forceVisible={!isModalOpen}
        margin={36}
        message={message}
        position="right"
      >
        {children}
      </Tooltip>
    );
  }

  handleClickClear(evt) {
    preventDefault(evt);
    this.props.onClear();
  }

  handleClickViewMatch(evt) {
    preventDefault(evt);
    this.props.onOpenModal();
  }
}

MergeableErrorTooltip.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  customerId: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  isModalOpen: PropTypes.bool,
  onClear: PropTypes.func,
  onOpenModal: PropTypes.func.isRequired,
  onTooltipRender: PropTypes.func,
  showClearButton: PropTypes.bool,
  showTooltip: PropTypes.bool,
};

const MergeableErrorTooltipContainer = connect(mapStateToProps, mapExecuteToProps)(MergeableErrorTooltip);
export default MergeableErrorTooltipContainer;

MergeableErrorTooltipContainer.propTypes = {
  attr: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  onClear: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

function mapStateToProps({ getProvider }, props) {
  const isModalOpen = !!getProvider('modal').get();

  return {
    ...props,
    isModalOpen,
    showTooltip: !!_.find(props.errors, err => err.code === Err.Code.TAKEN && _.get(err, 'meta.customerId')),
    showClearButton: true,
  };
}

function mapExecuteToProps(executeAction, props) {
  return {
    onOpenModal: () => {
      const mergeableError = _.find(props.errors, err => err.code === Err.Code.TAKEN && _.get(err, 'meta.customerId'));
      const takenCustomerId = _.get(mergeableError, 'meta.customerId');
      if (!takenCustomerId) {
        return;
      }

      const split = mergeableError.attr.split('.');
      const index = Number.parseInt(split[1]);

      let value = props.value;
      if (props.attr === 'phone') {
        try {
          value = normalizePhoneNumber(value);
        } catch (error) {
          // No big deal, just render it how it is
        }
      } else if (props.attr === 'email') {
        value = normalizeEmailAddress(value);
      }

      executeAction(
        OpenModal,
        new MergeProfile({
          customerId: takenCustomerId,
          pendingDetail: {
            [props.attr]: value,
            index,
          },
        })
      );
    },
  };
}

export const MatchTooltipContainer = connect(matchMapStateToProps, matchMapExecuteToProps)(MergeableErrorTooltip);

MatchTooltipContainer.propTypes = {
  children: PropTypes.node,
};

function matchMapStateToProps({ getProvider }, { className, children, customerId }) {
  const customerMatchProvider = getProvider('customerMatch');
  const isModalOpen = !!getProvider('modal').get();

  return {
    children,
    className,
    customerId,
    isModalOpen,
    showTooltip: customerMatchProvider.get() && customerMatchProvider.get().profiles.length > 0,
    showClearButton: false,
  };
}

function matchMapExecuteToProps(executeAction, { customerId }) {
  return {
    onTooltipRender: nextCustomerId => {
      executeAction(TrackTooltipShown, { customerId: nextCustomerId });
    },
    onOpenModal: () => {
      executeAction(OpenModal, new MergeProfile());
      executeAction(ShowMatches, customerId);
    },
  };
}

function preventDefault(evt) {
  evt.preventDefault();
  evt.stopPropagation();
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/profile/contact_info/mergeable_error_tooltip.jsx